import { projectBySlug, topicBySlug, type Topic, type WorkshopProject } from '@/data/profile';

export type ResolvedPage =
  | { kind: 'home' }
  | { kind: 'topic'; topic: Topic; project?: WorkshopProject }
  | { kind: 'notfound' };

export function resolvePage(path: string): ResolvedPage {
  const normalized = normalizePath(path);
  if (normalized === '/') {
    return { kind: 'home' };
  }

  const parts = normalized.slice(1).split('/');
  const topic = topicBySlug(parts[0] ?? '');
  if (!topic) {
    return { kind: 'notfound' };
  }

  if (parts.length === 1) {
    return { kind: 'topic', topic };
  }

  if (parts.length === 2) {
    const project = projectBySlug(topic, parts[1] ?? '');
    if (project) {
      return { kind: 'topic', topic, project };
    }
  }

  return { kind: 'notfound' };
}

export function normalizePath(url: string): string {
  const path = (url.split('?')[0] ?? '/').split('#')[0] || '/';
  if (path.length > 1 && path.endsWith('/')) {
    return path.slice(0, -1);
  }
  return path || '/';
}

export function applySeo(template: string, path: string, title: string, description: string): string {
  const canonical = path === '/' ? 'https://swezey.io/' : `https://swezey.io${path}`;
  return template
    .replace(/<title>[\s\S]*?<\/title>/, `<title>${title}</title>`)
    .replace(/(name="description" content=")[^"]*(")/, `$1${description}$2`)
    .replace(/(property="og:title" content=")[^"]*(")/, `$1${title}$2`)
    .replace(/(property="og:description" content=")[^"]*(")/, `$1${description}$2`)
    .replace(/(name="twitter:title" content=")[^"]*(")/, `$1${title}$2`)
    .replace(/(name="twitter:description" content=")[^"]*(")/, `$1${description}$2`)
    .replace(/(rel="canonical" href=")[^"]*(")/, `$1${canonical}$2`)
    .replace(/(property="og:url" content=")[^"]*(")/, `$1${canonical}$2`);
}

export function pageMeta(path: string): { title: string; description: string } {
  if (path === '/') {
    return {
      title: 'Matt Swezey',
      description: 'Matt Swezey writes software in Austin.',
    };
  }

  const page = resolvePage(path);
  if (page.kind === 'topic') {
    if (page.project) {
      return {
        title: `${page.project.label} — Matt Swezey`,
        description: page.topic.description,
      };
    }
    return {
      title: `${page.topic.label} — Matt Swezey`,
      description: page.topic.description,
    };
  }

  return {
    title: 'Not found — Matt Swezey',
    description: 'Page not found.',
  };
}
